import {
  Drawer,
  DrawerProps,
  InfiniteScrollContainer,
  UploadDropZone,
  UploadsButton,
  useUploadConfiguration,
  useUploadManager,
} from '@design-stack-vista/upload-components'
import {
  Box,
  Button,
  Divider,
  FlexBox,
  Icon,
  Link,
  ModalDialogBody,
  ModalDialogCloseButton,
  ModalDialogContent,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogNav,
  ModalDialogTitle,
  tokens,
  Typography,
} from '@vp/swan'
import { useSelector } from 'react-redux'
import { useEffect, useRef, useState } from 'react'
import equal from 'fast-deep-equal'
import { useIdentity } from '@vp/ubik-context'
import { IMAGE_UPLOAD_STATES, MAX_PHOTO_UPLOADS } from 'client/constants'
import { getLogger } from 'shared/logger'
import { RenderProperty } from 'shared/renderProperties'
import { DismissableAlertBox } from '~/client/components/common/DismissableAlertBox'
import { booleanRenderPropertySelector, stringRenderPropertySelector } from '~/client/store/config'
// TODO T021
// import { useExperimentation } from '~/client/hooks/useExperimentation';
// import { LOGOMAKER_CTA_VARIATIONS } from '~/experiments/LogomakerCTA/constants';
// import { ANALYTICS_EVENT_ACTIONS } from '~/shared/constants';
// import { useAnalytics } from '~/client/hooks/gallery/useAnalytics';
import { SignInButton } from '../../../../../common/SignInButton'
import { UploadsModalSubmitButton } from './UploadsModalSubmitButton'
import { useUploadsStore } from '../../UploadsProvider'
import { useDrawerHandlers } from '../../hooks/useUploadsDrawerHandlers'
import { SCHEME_KEYS, usePersonalizationManager } from '../../PersonalizationManager'
import { useSchemeUploads } from '../../../Schemes/hooks/useSchemeUploads'

export interface UploadsModalContentProps {
  handleSignIn: () => void
  handleSubmit: () => void
  handleClose: () => void
  isOpen: boolean
  handleBack?: () => void
  isMobile?: boolean
}

export const UploadsModalContent = (props: UploadsModalContentProps) => {
  const { handleSignIn, handleSubmit, isOpen, isMobile, handleClose, handleBack } = props
  const { uploads } = useSchemeUploads(SCHEME_KEYS.MANUAL)

  const { translations, tracking } = usePersonalizationManager()
  const { uploadsCopy } = translations

  const LOGGER = getLogger()

  const { hasAssets } = useUploadManager()
  // const analytics = useAnalytics();
  const { imageUploadState, selectedUploads, setImageUploadState, setSelectedUploads } = useUploadsStore()

  const uploadsButtonRef = useRef<HTMLInputElement>(null)

  // THe follow state is to handle the fact InfinteScrollContainer needs to be in a fixed height div
  // This is a terrible hack and not recommended
  // TODO: QSP-793 clean this up after the underlying issues is addressed by https://vistaprint.atlassian.net/browse/DKT-389
  const [usedHeight, setUsedHeight] = useState(0)
  const navRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const errorRef = useRef<HTMLDivElement>(null)
  const uploadsZoneRef = useRef<HTMLDivElement>(null)
  const hasAssetsHeaderRef = useRef<HTMLDivElement>(null)
  const previewButtonRef = useRef<HTMLDivElement>(null)
  const footerRef = useRef<HTMLDivElement>(null)
  const PREVIEW_BUTTON_TO_BOTTOM_MIN_MARGIN = tokens.SwanSemSpace5

  // const isExperimentActive = useExperimentation();
  // const isLogomakerCtaExperimentEnabled = isExperimentActive(
  //     LOGOMAKER_CTA_VARIATIONS.Enabled,
  // );

  const { isSignedIn } = useIdentity()
  const {
    supportedFileTypes: { fileExtensionsAsString },
  } = useUploadConfiguration()
  const updatedFileExtensions = fileExtensionsAsString
    .split(', ')
    .filter((ext: string) => ext !== '.pdf')
    .join(', ')

  const stringRenderPropSelector = useSelector(stringRenderPropertySelector)
  const imageCopyKey = stringRenderPropSelector(RenderProperty.PersonalizationImageCopy)
  const usePhotoText = imageCopyKey === 'Photo'
  const acceptedFormatsCopy = usePhotoText
    ? `${uploadsCopy.acceptedFormatsMessagePhoto} ${updatedFileExtensions}`
    : `${uploadsCopy.acceptedFormatsMessage} ${updatedFileExtensions}`

  const booleanRenderProperty = useSelector(booleanRenderPropertySelector)
  const smartCropEnabled = booleanRenderProperty(RenderProperty.PersonalizationSmartCropEnabled)

  /*
   * Workaround for cases where elements within modal have too much height due to copy wrapping to next line
   * in header and footer
   * More of the terrible hack
   * TODO: QSP-793 clean this up
   * Also on mobile which has compressed spacing, which allows for more space for the drawer
   */
  useEffect(() => {
    const margin = 24 + 16 + 32 + 24 + 24 + 8
    const calc =
      (navRef.current?.getBoundingClientRect().height ?? 0) +
      (headerRef.current?.getBoundingClientRect().height ?? 0) +
      // an additional hack to account for the margin on the error popover
      (errorRef.current?.getBoundingClientRect().height ?? -24) +
      (uploadsZoneRef.current?.getBoundingClientRect().height ?? 0) +
      (hasAssetsHeaderRef.current?.getBoundingClientRect().height ?? 0) +
      (previewButtonRef.current?.getBoundingClientRect().height ?? 0) +
      (footerRef.current?.getBoundingClientRect().height ?? 0) +
      margin

    setUsedHeight(calc)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    setUsedHeight,
    isOpen,
    imageUploadState.status,
    navRef.current,
    headerRef.current,
    errorRef.current,
    uploadsZoneRef.current,
    hasAssetsHeaderRef.current,
    previewButtonRef.current,
    footerRef.current,
  ])

  const { handleImageLoadStart, handleImageLoadComplete, handleImageLoadError } = useDrawerHandlers()

  const handleDrawerClick: DrawerProps['onClick'] = async (asset, pageNumber) => {
    try {
      const assetIndexInSelection = selectedUploads.findIndex((upload) => upload.asset.data?.id === asset.data?.id && (upload.pageNumber ?? 1) === pageNumber)
      let newUploadSelection

      if (assetIndexInSelection > -1) {
        // asset was selected, unselect it
        newUploadSelection = [...selectedUploads.slice(0, assetIndexInSelection), ...selectedUploads.slice(assetIndexInSelection + 1)]
        tracking.imageDeselected()
      } else {
        if (smartCropEnabled) {
          const saliencyMapUrl = asset.getStackedVariantUrl({
            variants: [{ variantName: 'saliencyMap' }],
            includeSignature: true,
          })
          fetch(saliencyMapUrl)
        }
        newUploadSelection = [...selectedUploads, { asset, pageNumber }]
        tracking.imageSelected()
      }

      setSelectedUploads(newUploadSelection)
    } catch (e) {
      LOGGER.error(`Failed to select photos to preview: ${e}`, e as Error, { asset })
      setImageUploadState({
        status: IMAGE_UPLOAD_STATES.ERROR,
        message: uploadsCopy.generalError,
      })
    }
  }

  const handleUploadsClicked = (): void => {
    tracking.imageUploadClicked()
  }

  const handleClearSelectedImages = (): void => {
    tracking.selectedImagesCleared()
    setSelectedUploads([])
  }

  useEffect(() => {
    const imageButtonFocusTimeout = setTimeout(() => {
      if (isOpen) {
        uploadsButtonRef.current?.focus()
        setImageUploadState({ status: IMAGE_UPLOAD_STATES.READY })
      }
    }, 100)

    // Cleanup timeout on unmount or dependency change
    return () => clearTimeout(imageButtonFocusTimeout)
  }, [isOpen, setImageUploadState])

  const cantApply = selectedUploads.length > MAX_PHOTO_UPLOADS || equal(selectedUploads, uploads)

  // const onLogmakerCtaClick = (): void => {
  //     analytics.trackEvent({
  //         action: ANALYTICS_EVENT_ACTIONS.EXPERIMENT_CLICKED,
  //         eventLabel: 'Personalization Upload Modal',
  //         eventDetail: 'Personalization Upload Modal: Create A Free Logo',
  //         navigationDetail: 'createAFreeLogo',
  //     });
  // };

  return (
    <ModalDialogContent>
      <ModalDialogNav mb={0} pb={isMobile ? 0 : 7} ref={navRef}>
        {handleBack && (
          <Button className="personalization-mobile-back-button" skin="unstyled" onClick={handleBack}>
            <Icon iconType="arrowLeft" pr={1} />
            {uploadsCopy.backButtonText}
          </Button>
        )}
        <ModalDialogCloseButton accessibleText={uploadsCopy.closeButtonText} aria-label={uploadsCopy.closeButtonText} onClick={handleClose} />
      </ModalDialogNav>
      <Box className="personalization-modal-content" px={isMobile ? 0 : 2}>
        <ModalDialogHeader mb={isMobile ? 'to-actions' : 7} mt={isMobile ? 6 : undefined} ref={headerRef}>
          <ModalDialogTitle
            className={isMobile ? 'personalization-mobile-modal-header' : 'personalization-modal-header'}
            fontSkin="title-section"
            textAlign="center"
          >
            {uploadsCopy.modalImageHeaderText}
          </ModalDialogTitle>
        </ModalDialogHeader>
        {imageUploadState.status === IMAGE_UPLOAD_STATES.ERROR && (
          <Box ref={errorRef}>
            <DismissableAlertBox
              toast
              className="personalization-image-alert"
              dismissVisuallyHiddenLabel={uploadsCopy.dismissAlert}
              marginBottom="to-actions"
              skin="error"
              onRequestDismiss={(): void => setImageUploadState({ status: IMAGE_UPLOAD_STATES.READY })}
            >
              {uploadsCopy.imageUploadErrorMessage}
            </DismissableAlertBox>
          </Box>
        )}
        <ModalDialogBody>
          <Box ref={uploadsZoneRef}>
            <UploadDropZone noClick noKeyboard>
              <UploadsButton
                multiple
                disabled={imageUploadState.status === IMAGE_UPLOAD_STATES.LOADING}
                iconPosition="left"
                ref={uploadsButtonRef}
                skin={hasAssets && !cantApply ? 'secondary' : 'primary'}
                width="full-width"
                onClick={handleUploadsClicked}
              >
                <Icon iconType="upload" size="20p" />
                <span>{uploadsCopy.imageUploadButtonText}</span>
              </UploadsButton>
            </UploadDropZone>
            <Typography className="accepted-formats" fontSize="small" mt="4" textColor="subtle">
              {acceptedFormatsCopy}
            </Typography>
          </Box>
          {hasAssets && (
            <>
              <Box ref={hasAssetsHeaderRef}>
                <Divider mt={isMobile ? 'between-subsections' : 7} />
                <FlexBox alignItems="center" justifyContent="space-between" my={5}>
                  <Typography fontSize="small" fontWeight="bold">
                    {uploadsCopy.uploadedAssetsTitle}
                  </Typography>
                  {selectedUploads.length > 0 && (
                    <Link
                      component="button"
                      fontSize="small"
                      render={(p) => (
                        <Button className={p.className} skin="link" onClick={handleClearSelectedImages}>
                          {p.children}
                        </Button>
                      )}
                    >
                      {uploadsCopy.clearSelection}
                    </Link>
                  )}
                </FlexBox>
              </Box>
              <Box
                className={!isSignedIn ? 'personalization-drawer-small-container' : 'personalization-drawer-container'}
                mb="to-actions"
                // This is a hack to deal with InfiniteScrollContainer requiring a fixed size wrapper
                style={{
                  maxHeight: `calc(100dvh - ${PREVIEW_BUTTON_TO_BOTTOM_MIN_MARGIN} - ${usedHeight}px)`,
                }}
              >
                <InfiniteScrollContainer infiniteScrollId="personalization-scroll-list-container">
                  <Drawer
                    deleteOption="confirm"
                    emptyResultsElement={<></>}
                    styleOptions={{ thumbnailSize: 102, flexboxGap: 12 }}
                    usedAssets={selectedUploads.map((upload) => ({ assetId: upload.asset.data?.id || '', pages: [upload.pageNumber ?? 1] }))}
                    onClick={handleDrawerClick}
                    onImageLoadCompleted={handleImageLoadComplete}
                    onImageLoadError={handleImageLoadError}
                    onImageLoadStart={handleImageLoadStart}
                  />
                </InfiniteScrollContainer>
              </Box>
              <Box ref={previewButtonRef}>
                <UploadsModalSubmitButton disabled={cantApply} handleSubmit={handleSubmit} />
              </Box>
            </>
          )}
        </ModalDialogBody>
        {/* {isLogomakerCtaExperimentEnabled && (
                    <Box>
                        {hasAssets && <Divider my={7} />}
                        <Card bordered mb={7}>
                            <Typography fontSkin="title-subsection">
                                Need a logo?
                            </Typography>
                            <Typography fontSkin="body-standard" mt="to-actions">
                                Create one in minutes and print with confidence. No
                                design skills required. It&apos;s quick, easy and free
                                from start to finish.
                            </Typography>
                            <Button
                                iconPosition="left"
                                mt="to-actions"
                                render={(linkProps: LinkProps) => (
                                    <Link
                                        className={linkProps.className}
                                        target="_blank"
                                        to="/logomaker"
                                        onClick={onLogmakerCtaClick}
                                    >
                                        {linkProps.children}
                                    </Link>
                                )}
                                width="full-width"
                            >
                                <Icon alt="next step" iconType="ai" />
                                Create a free logo
                            </Button>
                        </Card>
                    </Box>
                )} */}
      </Box>
      {!isSignedIn && (
        <ModalDialogFooter pinned className="personalization-modal-footer" py={isMobile ? 0 : 5} ref={footerRef}>
          <Box
            className="personalization-modal-footer-content"
            mb={isMobile ? 5 : undefined}
            pt={isMobile ? 6 : undefined}
            py={isMobile ? undefined : 4}
            textAlign="center"
          >
            <Typography fontSkin="body-small" mb={isMobile ? 4 : 5} textAlign="center">
              {uploadsCopy.logInDescription}
            </Typography>
            <SignInButton
              beforeSignIn={handleSignIn}
              className="personalization-modal-sign-in-button"
              component="button"
              fontSkin="body-standard-bold"
              signInText={uploadsCopy.logInButtonText}
              skin="cta"
            />
          </Box>
        </ModalDialogFooter>
      )}
    </ModalDialogContent>
  )
}
